import { useState } from "react";
import { format } from "date-fns";
import { nb } from "date-fns/locale";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
  UserIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import { PMButton } from "../../utils/PMButton";
import { WorkOrderEventDialog } from "./work-order-event-dialog";

export const WorkOrderEventsSection = ({
  workOrder,
  events = [],
  canEdit = false,
  onEventsMutated,
  onRefresh,
}) => {
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (event) => {
    setSelectedEvent(event);
    setEventDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setEventDialogOpen(false);
    setSelectedEvent(null);
  };

  const formatEventTime = (startTime, endTime, allDay, workingHours) => {
    if (allDay) {
      return format(new Date(startTime), "EEEE, d. MMMM yyyy", { locale: nb });
    }

    const start = new Date(startTime);
    const end = new Date(endTime);

    // Check if this is a multi-day event with working hours
    if (
      workingHours &&
      workingHours.type === "all_day" &&
      workingHours.schedule
    ) {
      const workingDays = workingHours.schedule.filter((day) => !day.day_off);
      const totalHours = workingHours.total_hours || 0;

      // Show full period for multi-day events
      const startDate = format(start, "d. MMMM yyyy", { locale: nb });
      const endDate = format(end, "d. MMMM yyyy", { locale: nb });

      if (startDate === endDate) {
        // Same day event
        return (
          format(start, "EEEE, d. MMMM yyyy 'kl.' HH:mm", { locale: nb }) +
          " - " +
          format(end, "HH:mm", { locale: nb })
        );
      } else {
        // Multi-day event
        return `${startDate} - ${endDate} (${workingDays.length} dag${
          workingDays.length !== 1 ? "er" : ""
        }, ${totalHours} timer)`;
      }
    }

    // Default format for single-day events
    const startFormatted = format(start, "EEEE, d. MMMM yyyy 'kl.' HH:mm", {
      locale: nb,
    });
    const endFormatted = format(end, "HH:mm", { locale: nb });
    return `${startFormatted} - ${endFormatted}`;
  };

  const getEventStatusColor = (status) => {
    switch (status) {
      case "active":
        return "bg-green-100 text-green-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-blue-100 text-blue-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="mt-5 shadow sm:overflow-hidden sm:rounded-md">
      <div className="space-y-6 bg-white py-6 px-4 sm:p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Hendelser
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {events.length} hendelse{events.length !== 1 ? "r" : ""} knyttet
              til denne arbeidsordren
            </p>
          </div>
          {canEdit && (
            <PMButton
              onClick={handleCreateEvent}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 text-sm"
            >
              <PlusIcon className="h-4 w-4 mr-1" />
              Opprett hendelse
            </PMButton>
          )}
        </div>

        {events.length === 0 ? (
          <div className="text-center py-8">
            <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              Ingen hendelser
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {canEdit
                ? "Opprett din første hendelse for denne arbeidsordren."
                : "Ingen hendelser er knyttet til denne arbeidsordren."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {events.map((event) => (
              <div
                key={event.id}
                className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="text-lg font-medium text-gray-900">
                        {event.title}
                      </h4>
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getEventStatusColor(
                          event.status
                        )}`}
                      >
                        {event.status === "active"
                          ? "Aktiv"
                          : event.status === "cancelled"
                          ? "Kansellert"
                          : event.status === "completed"
                          ? "Fullført"
                          : event.status}
                      </span>
                    </div>

                    {event.description && (
                      <p className="text-sm text-gray-600 mb-3">
                        {event.description}
                      </p>
                    )}

                    <div className="space-y-1 text-sm text-gray-500">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="h-4 w-4" />
                        <span>
                          {formatEventTime(
                            event.start_time,
                            event.end_time,
                            event.all_day,
                            event.working_hours
                          )}
                        </span>
                      </div>

                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPinIcon className="h-4 w-4" />
                          <span>{event.location}</span>
                        </div>
                      )}

                      {/* Show Working Hours if available */}
                      {event.working_hours && (
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4" />
                          <span className="text-gray-500">
                            <span className="font-medium">Arbeidstid:</span>{" "}
                            {(() => {
                              // Handle new backend format (single day with specific times)
                              if (
                                event.working_hours.date &&
                                event.working_hours.type === "specific_time"
                              ) {
                                const totalHours =
                                  event.working_hours.total_hours || 0;
                                return `${totalHours} timer (${event.working_hours.start_time} - ${event.working_hours.end_time})`;
                              }
                              // Handle new JSONB format with schedule array
                              else if (
                                event.working_hours.schedule &&
                                Array.isArray(event.working_hours.schedule)
                              ) {
                                // Stale multi-day plan can remain if event was switched to timed — prefer wall clock
                                if (
                                  !event.all_day &&
                                  event.start_time &&
                                  event.end_time
                                ) {
                                  const ms =
                                    new Date(event.end_time).getTime() -
                                    new Date(event.start_time).getTime();
                                  if (Number.isFinite(ms) && ms > 0) {
                                    const h = Math.round((ms / 3600000) * 100) / 100;
                                    return `${h} timer (etter start/slutt)`;
                                  }
                                }
                                const workingDays =
                                  event.working_hours.schedule.filter(
                                    (day) => !day.day_off
                                  );
                                const totalHours =
                                  event.working_hours.total_hours || 0;
                                return `${workingDays.length} dag${
                                  workingDays.length !== 1 ? "er" : ""
                                } (${totalHours} timer)`;
                              }
                              // Handle old format (object with date keys)
                              else if (
                                typeof event.working_hours === "object" &&
                                !Array.isArray(event.working_hours) &&
                                !event.working_hours.date
                              ) {
                                const workingDays = Object.values(
                                  event.working_hours
                                ).filter((day) => !day.off);
                                return `${workingDays.length} dag${
                                  workingDays.length !== 1 ? "er" : ""
                                }`;
                              }
                              // Handle numeric format (old)
                              else if (
                                typeof event.working_hours === "number"
                              ) {
                                return `${event.working_hours} timer`;
                              }
                              return "Ikke spesifisert";
                            })()}
                          </span>
                        </div>
                      )}

                      <div className="flex items-center gap-2">
                        <UserIcon className="h-4 w-4" />
                        <span>
                          {event.responsible_executor ? (
                            <>
                              <span className="font-medium">
                                Ansvarlig utførende:
                              </span>{" "}
                              {event.responsible_executor}
                            </>
                          ) : (
                            event.user?.displayTitle || "Ukjent bruker"
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {canEdit && (
                    <div className="flex items-center gap-2 ml-4">
                      <button
                        onClick={() => handleEditEvent(event)}
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        title="Rediger hendelse"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Event Dialog */}
      <WorkOrderEventDialog
        open={eventDialogOpen}
        onClose={handleCloseDialog}
        workOrder={workOrder}
        event={selectedEvent}
        onEventsMutated={onEventsMutated}
        onRefresh={onRefresh}
      />
    </div>
  );
};
